import {
  Box,
  Button,
  Header,
  SpaceBetween,
  Container,
  FormField,
  Input,
  ColumnLayout,
  Flashbar,
  TokenGroup,
  TokenGroupProps,
} from '@amzn/awsui-components-console';
import { useState, useEffect, useContext } from 'react';
import {
  LaunchTask,
  Action,
  UpdatePermissionsCommand,
  UpdatePermissionsCommandOutput,
  UpdatePermissionsCommandInput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../../../authentication/context';
import { callApi } from '../../../client';
import { PermissionsContext } from './permissionscontext';
import { NoTokenError } from '../../../errors/NoTokenError';
import { useApiErrors } from '../../../components/ApiErrorContext';

interface editProps {
  launchTaskDetail: LaunchTask;
}

export const ModificationView = ({ launchTaskDetail }: editProps) => {
  const {
    readPermissions,
    writePermissions,
    updateReadPermissions,
    updateWritePermissions,
    notifications,
    setNotifications,
  } = useContext(PermissionsContext);
  const readerId = 'updateReaderMsg';
  const writerId = 'updateWriterMsg';
  const { pushError } = useApiErrors();

  const [newReader, setNewReader] = useState('');
  const [errorIndicatorReader, setErrorIndicatorReader] = useState('');
  const [errorIndicatorWriter, setErrorIndicatorWriter] = useState('');
  const [readerList, setReaderList] = useState<TokenGroupProps.Item[]>([]);
  const [newWriter, setNewWriter] = useState('');
  const [writerList, setWriterList] = useState<TokenGroupProps.Item[]>([]);

  useEffect(() => {
    if (newReader) {
      if (newReader.includes(' ')) {
        setErrorIndicatorReader('Cannot have space in between');
      } else {
        setErrorIndicatorReader('');
      }
    }
  }, [newReader]);

  useEffect(() => {
    if (newWriter) {
      if (newWriter.includes(' ')) {
        setErrorIndicatorWriter('Cannot have space in between');
      } else {
        setErrorIndicatorWriter('');
      }
    }
  }, [newWriter]);

  const addReader = (newReader: string) => {
    const newItem: TokenGroupProps.Item = { label: newReader };
    if (!readerList.some((item) => item.label === newReader)) {
      setReaderList([...readerList, newItem]);
    }
  };

  const generateRequestBody = (action: Action) => {
    const newReadPermissions = [...readPermissions];
    const newWritePermissions = [...writePermissions];

    if (action == 'READ') {
      for (const item of readerList) {
        const label = item.label;
        if (label && !newReadPermissions.includes(label))
          newReadPermissions.push(label);
      }
    } else if (action == 'WRITE') {
      for (const item of writerList) {
        const label = item.label;
        if (label && !newWritePermissions.includes(label))
          newWritePermissions.push(label);
      }
    }

    const body: UpdatePermissionsCommandInput = {
      launchId: launchTaskDetail.launchId,
      readAcl: newReadPermissions,
      writeAcl: newWritePermissions,
    };

    return body;
  };

  const { isAuthenticated, token } = useAuthState();

  const addWriter = (newWriter: string) => {
    const newItem: TokenGroupProps.Item = { label: newWriter };
    if (!writerList.some((item) => item.label === newWriter)) {
      setWriterList([...writerList, newItem]);
    }
  };

  async function callGrantPermissionReadApi(
    body: UpdatePermissionsCommandInput
  ) {
    if (!token) throw new NoTokenError();
    try {
      const response: UpdatePermissionsCommandOutput = await callApi(
        new UpdatePermissionsCommand({
          launchId: body.launchId,
          readAcl: body.readAcl,
          writeAcl: body.writeAcl,
        }),
        token
      );
      if (response.$metadata.httpStatusCode == 200) {
        updateReadPermissions(body.readAcl);
        setNotifications((prev) => [
          ...prev.filter((item) => item.id !== readerId),
          {
            id: readerId,
            type: 'success',
            content: 'updating readers permission succeed',
            dismissible: true,
            dismissLabel: 'Dismiss',
            onDismiss: () =>
              setNotifications((items) =>
                items.filter((item) => item.id !== readerId)
              ),
          },
        ]);
      }
    } catch (err: any) {
      pushError(
        'Grant Read Permission Failed',
        err?.message || 'Failed to grant read permission'
      );
    }
  }

  async function callGrantPermissionWriteApi(
    body: UpdatePermissionsCommandInput
  ) {
    if (!token) throw new NoTokenError();
    try {
      const response: UpdatePermissionsCommandOutput = await callApi(
        new UpdatePermissionsCommand({
          launchId: body.launchId,
          readAcl: body.readAcl,
          writeAcl: body.writeAcl,
        }),
        token
      );
      if (response.$metadata.httpStatusCode == 200) {
        updateWritePermissions(body.writeAcl);
        setNotifications((prev) => [
          ...prev.filter((item) => item.id !== writerId),
          {
            id: writerId,
            type: 'success',
            content: 'updating writers permission succeed',
            dismissible: true,
            dismissLabel: 'Dismiss',
            onDismiss: () =>
              setNotifications((items) =>
                items.filter((item) => item.id !== writerId)
              ),
          },
        ]);
      }
    } catch (err: any) {
      pushError(
        'Grant Write Permission Failed',
        err?.message || 'Failed to grant write permission'
      );
    }
  }

  return (
    <div>
      <SpaceBetween direction='vertical' size='s'>
        <SpaceBetween direction='vertical' size='s'>
          <Box>
            <Flashbar
              items={notifications}
              i18nStrings={{
                ariaLabel: 'Notifications',
                notificationBarAriaLabel: 'View all notifications',
                notificationBarText: 'Notifications',
                errorIconAriaLabel: 'Error',
                warningIconAriaLabel: 'Warning',
                successIconAriaLabel: 'Success',
                infoIconAriaLabel: 'Info',
                inProgressIconAriaLabel: 'In progress',
              }}
              stackItems
            />
          </Box>
          <Container>
            <SpaceBetween size='s' direction='vertical'>
              <Container header={<Header>Assign read permissions</Header>}>
                <FormField
                  label='User alias or POSIX group'
                  errorText={errorIndicatorReader}
                >
                  <SpaceBetween direction='vertical' size='l'>
                    <ColumnLayout columns={2}>
                      <Input
                        value={newReader}
                        onChange={({ detail }) => {
                          setNewReader(detail.value);
                        }}
                      />
                      <Button
                        variant='primary'
                        disabled={errorIndicatorReader !== ''}
                        onClick={() => {
                          if (newReader != '') addReader(newReader);
                        }}
                      >
                        Add
                      </Button>
                    </ColumnLayout>
                  </SpaceBetween>
                </FormField>
              </Container>
              <Box display={readerList.length > 0 ? 'block' : 'none'}>
                <Container
                  header={
                    <Header
                      actions={
                        <SpaceBetween direction='horizontal' size='l'>
                          <Button
                            variant='primary'
                            onClick={() => {
                              const body = generateRequestBody('READ');
                              void callGrantPermissionReadApi(body);
                              setReaderList([]);
                            }}
                          >
                            Save
                          </Button>
                          <Button onClick={() => setReaderList([])}>
                            Cancel
                          </Button>
                        </SpaceBetween>
                      }
                    >
                      Grant read permission
                    </Header>
                  }
                >
                  <TokenGroup
                    onDismiss={({ detail: { itemIndex } }) => {
                      setReaderList([
                        ...readerList.slice(0, itemIndex),
                        ...readerList.slice(itemIndex + 1),
                      ]);
                    }}
                    items={readerList}
                  />
                </Container>
              </Box>
            </SpaceBetween>
          </Container>
        </SpaceBetween>

        <SpaceBetween direction='vertical' size='s'>
          <Container>
            <SpaceBetween size='s' direction='vertical'>
              <Container header={<Header>Assign write permissions</Header>}>
                <FormField
                  label='User alias or POSIX group'
                  errorText={errorIndicatorWriter}
                >
                  <SpaceBetween direction='vertical' size='l'>
                    <ColumnLayout columns={2}>
                      <Input
                        value={newWriter}
                        onChange={({ detail }) => {
                          setNewWriter(detail.value);
                        }}
                      />
                      <Button
                        variant='primary'
                        disabled={errorIndicatorWriter !== ''}
                        onClick={() => {
                          if (newWriter != '') addWriter(newWriter);
                        }}
                      >
                        Add
                      </Button>
                    </ColumnLayout>
                  </SpaceBetween>
                </FormField>
              </Container>
              <Box display={writerList.length > 0 ? 'block' : 'none'}>
                <Container
                  header={
                    <Header
                      actions={
                        <SpaceBetween direction='horizontal' size='l'>
                          <Button
                            variant='primary'
                            onClick={() => {
                              const body = generateRequestBody('WRITE');
                              void callGrantPermissionWriteApi(body);
                              setWriterList([]);
                            }}
                          >
                            Save
                          </Button>
                          <Button onClick={() => setWriterList([])}>
                            Cancel
                          </Button>
                        </SpaceBetween>
                      }
                    >
                      Grant write permission
                    </Header>
                  }
                >
                  <TokenGroup
                    onDismiss={({ detail: { itemIndex } }) => {
                      setWriterList([
                        ...writerList.slice(0, itemIndex),
                        ...writerList.slice(itemIndex + 1),
                      ]);
                    }}
                    items={writerList}
                  />
                </Container>
              </Box>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </SpaceBetween>
    </div>
  );
};

export default ModificationView;
